import { ApplicationConfig, importProvidersFrom, inject, provideBrowserGlobalErrorListeners } from '@angular/core';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { providePrimeNG } from 'primeng/config';
import { MessageService } from 'primeng/api';
import { ConfirmationService } from 'primeng/api';
import Aura from '@primeuix/themes/aura';
import {
  MsalService,
  MsalGuard,
  MsalBroadcastService,
  MSAL_INSTANCE,
  MSAL_GUARD_CONFIG,
  MSAL_INTERCEPTOR_CONFIG,
  MsalGuardConfiguration,
  MsalInterceptorConfiguration,
  MsalInterceptor,
} from '@azure/msal-angular';
import { IPublicClientApplication, PublicClientApplication, InteractionType, LogLevel } from '@azure/msal-browser';
import { LoggerModule, NGXLogger } from 'ngx-logger';

import { routes } from './app.routes';
import { msalConfig, loginRequest } from './core/auth/config/auth-config';
import { environment } from '../environments/environment';

/**
 * Factory function to create MSAL instance
 */
export function MSALInstanceFactory(): IPublicClientApplication {
  const logger = inject(NGXLogger);

  msalConfig!.system!.loggerOptions!.loggerCallback = (level: LogLevel, message: string, containsPii: boolean) => {
    if (containsPii) {
      return;
    }

    switch (level) {
      case LogLevel.Error:
        logger.error(message);
        return;
      case LogLevel.Info:
        logger.info(message);
        return;
      case LogLevel.Verbose:
        logger.debug(message);
        return;
      case LogLevel.Warning:
        logger.warn(message);
        return;
    }
  };

  return new PublicClientApplication(msalConfig);
}

/**
 * Factory function to create MSAL Guard configuration
 */
export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: loginRequest,
  };
}

/**
 * Factory function to create MSAL Interceptor configuration
 */
export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, string[] | null>([
    ['https://graph.microsoft.com/v1.0/', ['user.read', 'profile']],
    [`${environment.apiHost}/*`, [`api://${environment.azure.clientId}/access_as_user`]],
  ]);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideRouter(routes, withComponentInputBinding()),
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: Aura,
        options: {
          cssLayer: {
            name: 'primeng',
            order: 'theme, base, primeng',
          },
        },
      },
    }),
    importProvidersFrom(
      LoggerModule.forRoot({
        serverLoggingUrl: environment.logging.apiEndpoint,
        level: environment.logging.level,
        serverLogLevel: environment.logging.serverLogLevel,
      }),
    ),
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    {
      provide: HTTP_INTERCEPTORS, // Provides as HTTP Interceptor
      useClass: MsalInterceptor,
      multi: true,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    MessageService,
    ConfirmationService,
  ],
};
